import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';

export interface AccountEditDialogData {
  displayName: string;
}

export interface AccountEditDialogResult {
  displayName: string;
  password?: string;
}

@Component({
  selector: 'app-account-edit-dialog',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
  ],
  template: `
    <h2 mat-dialog-title>アカウントの編集</h2>
    <mat-dialog-content class="pt-4">
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-4">
        <mat-form-field appearance="outline" class="w-full mt-2">
          <mat-label>表示名</mat-label>
          <input matInput formControlName="displayName" placeholder="例: 山田 太郎">
        </mat-form-field>
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>新しいパスワード（変更する場合のみ）</mat-label>
          <input matInput formControlName="password" [type]="hidePassword() ? 'password' : 'text'" placeholder="6文字以上">
          <button matIconButton matSuffix type="button" (click)="togglePasswordVisibility()" [attr.aria-label]="hidePassword() ? 'パスワードを表示' : 'パスワードを隠す'" [attr.aria-pressed]="!hidePassword()">
            <mat-icon>{{hidePassword() ? 'visibility_off' : 'visibility'}}</mat-icon>
          </button>
          @if (form.controls.password.hasError('minlength')) {
            <mat-error>パスワードは6文字以上で入力してください</mat-error>
          }
        </mat-form-field>

      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="pb-4 pr-4">
      <button matButton (click)="onCancel()" type="button">キャンセル</button>
      <button matButton="tonal" color="primary" [disabled]="form.invalid" (click)="onSubmit()">保存</button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountEditDialogComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  public readonly dialogRef = inject(MatDialogRef<AccountEditDialogComponent, AccountEditDialogResult | undefined>);
  public readonly data = inject<AccountEditDialogData>(MAT_DIALOG_DATA);

  public readonly form = this.fb.group({
    displayName: [this.data.displayName ?? ''],
    password: ['', [Validators.minLength(6)]],
  });

  public readonly hidePassword = signal(true);

  public togglePasswordVisibility(): void {
    this.hidePassword.update(v => !v);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const result: AccountEditDialogResult = {
        displayName: this.form.controls.displayName.value,
      };
      const password = this.form.controls.password.value;
      if (password) {
        result.password = password;
      }
      this.dialogRef.close(result);
    }
  }
}
